import React from "react";
import { useContent } from "@thoughtbot/superglue";

import { BrandMark } from "@javascript/components/BrandMark";
import { MarketingFooter } from "@javascript/components/MarketingFooter";

const linkClass = "font-medium text-primary underline decoration-primary/40 underline-offset-4";

type TermsContent = {
  price?: string;
};

export default function TermsShow() {
  // Absent only if no product is recorded at all, which the catalog is built to
  // prevent. The wording degrades rather than leaving a hole in a legal page.
  const { price } = useContent<TermsContent>();
  const priceLabel = price ?? "a single one-time price";
  const priceReference = price ?? "price";

  return (
    <>
      <section className="px-4 py-12 sm:px-6 sm:py-20">
        <article className="mx-auto w-full max-w-6xl rounded-2xl border bg-background p-6 shadow-sm sm:p-10">
          <BrandMark size="md" className="font-serif font-semibold text-foreground" />

          <header className="mt-10 max-w-2xl">
            <p className="text-sm font-medium text-primary">Terms of Service</p>
            <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-5xl">What you get, and what we owe you.</h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              The agreement between you and Goldypaper: what you are buying, when you can have your money back, and how long your invitation stays online. Written to be read, not to be skipped.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              <time dateTime="2026-08-17">Last updated: 17 August 2026</time>
            </p>
          </header>

          <div className="mt-10 space-y-10 leading-relaxed text-foreground">
            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Who you are agreeing with</h2>
              <p className="mt-3">
                Goldypaper is run by Nadiar Syaripul, an independent software business in Bandung, Indonesia. One person, not a company. Using Goldypaper means you accept these terms; if you do not accept them, do not use it.
              </p>
              <p className="mt-3">
                Anything about these terms goes to{" "}
                <a className={linkClass} href="mailto:contact@goldypaper.com">contact@goldypaper.com</a>. We answer within 3 business days, Monday to Friday, Indonesian public holidays aside.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">What Goldypaper does</h2>
              <p className="mt-3">
                You pick a theme, fill in your wedding details, and publish an invitation at its own public web address. You share that address; guests open it and reply there. That is the whole product.
              </p>
              <p className="mt-3">
                Goldypaper does not print anything, does not post anything, and does not contact your guests for you. Nobody is emailed on your behalf.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Cookies and analytics</h2>
              <p className="mt-3">
                The marketing site and your dashboard use optional analytics cookies, and only after you accept them from the banner shown on first visit — declining is exactly as easy as accepting. Your invitation&rsquo;s public address and its RSVP page are never measured, whatever you choose. Change your mind anytime from &ldquo;Cookie preferences&rdquo; in the footer. The{" "}
                <a className={linkClass} href="/privacy">Privacy Policy</a>{" "}
                has the full detail.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Your account</h2>
              <p className="mt-3">
                Sign up with an email address you can actually read — it is how we reach you, and how we check a request is really from you. Keep your password to yourself. What happens under your account is your responsibility.
              </p>
              <p className="mt-3">
                You have to confirm your email address before you can save or publish an invitation. That gate is deliberate: it is what stops someone else&rsquo;s wedding from ending up under an address we cannot reach.
              </p>
              <p className="mt-3">
                You need to be old enough to enter into a contract where you live.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">What you publish</h2>
              <p className="mt-3">
                Your words and your photographs stay yours. You give us permission to store them, resize them, and show them — only to run Goldypaper for you. We do not use your wedding in advertising, and we do not show it anywhere you did not ask us to.
              </p>
              <p className="mt-3">
                You confirm you have the right to what you upload. Wedding photographs are usually owned by the photographer who took them, so check what your contract with them allows before uploading.
              </p>
              <p className="mt-3">
                Do not publish anything unlawful, anything that impersonates someone else, or anything that puts a person at risk.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Your guests&rsquo; replies</h2>
              <p className="mt-3">
                When a guest replies to your invitation, they are answering <em>you</em>. You invited them; we are holding their answer on your behalf. You decide who is invited and what you do with what they tell you.
              </p>
              <p className="mt-3">
                A guest who wants their reply taken down should ask you first. The{" "}
                <a className={linkClass} href="/privacy">Privacy Policy</a>{" "}
                explains how we handle it.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Paying</h2>
              <p className="mt-3">
                An invitation costs <strong className="font-semibold">{priceLabel}</strong>, paid once. That is the whole price, in one currency, wherever you are.
              </p>
              <ul className="mt-4 list-disc space-y-2 pl-5">
                <li>One payment for one invitation. No subscription, no renewal, and no second charge later.</li>
                <li>No charge per guest and no guest limit. What you pay does not change if fifty people reply or five hundred.</li>
                <li>Any sales tax or VAT owed where you live is already inside that {priceReference}. Nothing is added at checkout.</li>
                <li>Payment is handled by a merchant of record — a payment company that is the legal seller on your receipt and settles the tax. Your card details never reach Goldypaper.</li>
              </ul>
              <p className="mt-3">
                The price shown may include a limited-time promotional discount, applied automatically — there is no code to enter and nothing extra to do. Promotional pricing is temporary and can end at any time without advance notice; when it does, the standard price applies to invitations bought afterwards. An invitation you have already paid for is never affected by a price change, promotional or otherwise.
              </p>
            </section>

            {/* Anchored: a payment provider's onboarding form asks for a refund policy URL,
                and /terms#refunds answers it until the policy gets a route of its own. */}
            <section id="refunds" className="max-w-3xl scroll-mt-8">
              <h2 className="font-serif text-2xl font-semibold">Refunds</h2>
              <p className="mt-3">
                Full refund within 30 days of purchase, as long as the invitation has not been published. Publishing is what delivers the product — once your invitation is live at its public address, the sale is final.
              </p>
              <p className="mt-3">
                There is no case to argue and nothing to prove: either the invitation has been published or it has not, and you can see which from your own dashboard. Ask by emailing{" "}
                <a className={linkClass} href="mailto:contact@goldypaper.com">contact@goldypaper.com</a>{" "}
                from the address your account uses.
              </p>
              <p className="mt-3">
                Two things hold above this policy. Consumer protection law where you live may give you rights we cannot sign away, and where it does, those rights win. And because a merchant of record is the legal seller, it may refund a payment at its own discretion without asking us.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">How long your invitation stays online</h2>
              <p className="mt-3">
                Your invitation has no expiry date, and there is no recurring charge — you pay once. We guarantee access for at least 24 months from purchase. If Goldypaper ever shuts down, we will give at least 90 days&rsquo; notice and provide a full export of your invitation and guest responses before access ends.
              </p>
              <p className="mt-3">
                We do not say &ldquo;lifetime&rdquo;, because one person cannot honestly promise it. What you are buying is an invitation with no expiry and no further bills, and a shutdown you would be warned about and walk away from with your data.
              </p>
              <p className="mt-3">
                You do not have to wait for that to get a copy: the guest list export is in your dashboard and you can run it whenever you like.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Deleting your data</h2>
              <p className="mt-3">
                Email{" "}
                <a className={linkClass} href="mailto:contact@goldypaper.com">contact@goldypaper.com</a>{" "}
                from the address your account uses and say you want it gone. It is done within 30 days, and there is no archive waiting to restore it afterwards. Deleting your data does not refund a purchase, and a refunded purchase does not delete your data — ask for whichever you want.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">When we can take an invitation down</h2>
              <p className="mt-3">
                If an invitation breaks the rules above, harms someone, or is used to attack the service, we can unpublish it or close the account behind it. We will email you first and say why, unless doing so would be against the law or would let the harm continue.
              </p>
              <p className="mt-3">
                Deliberate abuse is the one case where we keep the money.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">What we do not promise</h2>
              <p className="mt-3">
                Goldypaper is run carefully, but it is run by one person, and we cannot promise it is never down. Maintenance happens, hosting providers fail, and outages happen at inconvenient times — including, in the worst case, on a wedding day.
              </p>
              <p className="mt-3">
                Keep your own copies of your photographs. Goldypaper is where they are shown, not where they are backed up.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">If something goes wrong</h2>
              <p className="mt-3">
                If we owe you something, what we owe is capped at what you paid for the invitation concerned. We are not liable for indirect losses — other costs of your wedding, or things you could not do because the invitation was unavailable.
              </p>
              <p className="mt-3">
                Where the law where you live does not allow a limit like this, the law wins and this paragraph does not apply to you.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">Changes to these terms</h2>
              <p className="mt-3">
                When these terms change, this page changes and the date at the top moves. If a change materially affects something you have already bought, we email you about it. Changes do not apply backwards to a purchase already made.
              </p>
            </section>

            <section className="max-w-3xl">
              <h2 className="font-serif text-2xl font-semibold">The law that applies</h2>
              <p className="mt-3">
                These terms are governed by Indonesian law, and Indonesian courts are where a dispute would be heard. That does not remove the consumer protection rights you have where you live — where those rights cannot be signed away, they stand.
              </p>
            </section>
          </div>
        </article>
      </section>

      <MarketingFooter illustration={false} />
    </>
  );
}
